import { Container, IDestroyOptions, Point, Sprite } from "pixi.js";
import { MapZipCode } from "./MapZipCode";
import { gsap } from "gsap";
import { MapArea } from "../models/map/MapArea";
import { IMarketplaceMapModel } from "../models/map/IMarketplaceMapModel";
import { MapFilter } from "../models/map/MapFilter";
import { nameof } from "bindable-data";
import { MapZonesViewDescription } from "../types/MapZonesViewDescription";
import { IMarketplaceController } from "../models/controller/IMarketplaceController";
import { IMarketplaceViewModel } from "../models/IMarketplaceViewModel";
import { IPlayerModel } from "../../../../common/models/IPlayerModel";
import { IGameViewModel } from "../../../../game/models/IGameViewModel";
import { Event } from "event-system";
import { IAnalyticProviderWrapper } from "../../../../../../analytics/IAnalyticProviderWrapper";
import { VirtualScreenEvent } from "../../../../../../analytics/events/VirtualScreenEvent";
import { DefaultMarketplacePin } from "./pins/DefaultMarketplacePin";
import { MyMarketplacePin } from "./pins/MyMarketplacePin";
import { IBuildingModel } from "../../../../common/models/Building/IBuildingModel";
import { MarketplaceResourcePackage } from "../../resources/MarketplaceResourcePackage";

export class MapZone extends Container {
    public readonly onMapZonePinClick: Event;
    private readonly marketplaceResources: MarketplaceResourcePackage;
    private readonly marketplaceController: IMarketplaceController;
    private readonly marketplaceMapModel: IMarketplaceMapModel;
    private readonly mapZoneConfig: MapZonesViewDescription;
    private readonly marketplaceMapViewModel: IMarketplaceViewModel;
    private readonly gameViewModel: IGameViewModel;
    private readonly playerModel: IPlayerModel;
    private readonly mapZoneData: MapArea;
    private readonly analyticsProvider: IAnalyticProviderWrapper;
    private zoomTargetPositionProp: Point;
    public get zoomTargetPosition(): Point {
        return this.zoomTargetPositionProp;
    }
    private mapZoneBg: Sprite;
    private pinsContainer: Container;
    private zipCodePinsContainer: Container;
    private selectedSprite: Sprite;
    private corner: Sprite;
    private zipCodes: Array<MapZipCode>;
    private zipCodesContainer: Container;
    private pin: DefaultMarketplacePin;
    private myPin: MyMarketplacePin;
    private isSelected: boolean;
    private pinIsAvailable: boolean;
    private focusOutAnimation: gsap.core.Timeline;
    private focusInAnimation: gsap.core.Timeline;

    public constructor(
        marketplaceResources: MarketplaceResourcePackage,
        marketplaceController: IMarketplaceController,
        marketplaceMapModel: IMarketplaceMapModel,
        marketplaceMapViewModel: IMarketplaceViewModel,
        playerModel: IPlayerModel,
        mapZoneConfig: MapZonesViewDescription,
        pinsContainer: Container,
        mapZoneBg: Sprite,
        mapZoneData: MapArea,
        analyticsProvider: IAnalyticProviderWrapper
    ) {
        super();
        this.marketplaceResources = marketplaceResources;
        this.marketplaceController = marketplaceController;
        this.marketplaceMapModel = marketplaceMapModel;
        this.marketplaceMapViewModel = marketplaceMapViewModel;
        this.playerModel = playerModel;
        this.mapZoneConfig = mapZoneConfig;
        this.pinsContainer = pinsContainer;
        this.mapZoneBg = mapZoneBg;
        this.mapZoneData = mapZoneData;
        this.analyticsProvider = analyticsProvider;
        this.onMapZonePinClick = new Event();
        this.zipCodes = [];
        this.isSelected = false;
        this.pinIsAvailable = true;
        this.focusOutAnimation = new gsap.core.Timeline();
        this.focusInAnimation = new gsap.core.Timeline();
        this.build();
        this.marketplaceMapModel.propertyChanged.add(this.onMarketplaceMapModelPropertyChanged, this);
        this.once("added", this.addedToStage.bind(this));
    }

    private async onMarketplaceMapModelPropertyChanged(model: IMarketplaceMapModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "zipCodes"):
                if (this.isSelected && this.marketplaceMapViewModel.currentMapFilter.value.offset === 0) {
                    const animationTimeline = new gsap.core.Timeline();
                    animationTimeline.add(() => this.zipCodes.forEach(zipCode => zipCode.updatePinsState()), "0.1");
                    await animationTimeline.play();
                    animationTimeline.kill();
                }
                break;
            case nameof(model, "buildings"):
                if (this.isSelected && this.marketplaceMapViewModel.currentMapFilter.value.offset === 0) {
                    if (this.myPin) {
                        this.myPin.visible = this.getMyBuildingForMp() != null;
                    }
                }
                break;
            case nameof(model, "areas"):
                const newAreaData = model.areas.value.find(item => item.area === this.mapZoneConfig.area);
                this.pin.visible = this.pinIsAvailable = newAreaData != null && newAreaData.buildingsAmount > 0;
                if (newAreaData != null) {
                    this.pin.updateBuildingsAmount(newAreaData.buildingsAmount);
                }
                break;
        }
    }

    private build(): void {
        this.corner = new Sprite(this.mapZoneConfig.mapZoneDescription.cornerTexture);
        this.corner.position = this.mapZoneConfig.mapZoneDescription.cornerPosition;
        this.addChild(this.corner);

        this.zipCodesContainer = new Container();
        this.addChild(this.zipCodesContainer);

        this.selectedSprite = new Sprite(this.mapZoneConfig.mapZoneDescription.selectedTexture);
        this.selectedSprite.position = this.mapZoneConfig.mapZoneDescription.selectedGlowPosition;
        this.addChild(this.selectedSprite);

        this.zipCodePinsContainer = new Container();
        this.zipCodePinsContainer.scale.set(MapZipCode.DEFAULT_SCALE);
        this.addChild(this.zipCodePinsContainer);

        this.pin = new DefaultMarketplacePin(this.marketplaceResources, this.mapZoneData?.buildingsAmount ?? 0);
        this.pin.anchor.set(0.5);
        this.pin.eventMode = "static";
        this.pin.on("pointerdown", () => this.onMapZonePinClick.emit());
        this.pin.position = this.mapZoneConfig.mapZoneDescription.otherPinPosition;
        this.pinsContainer.addChild(this.pin);

        const myBuildingOnMp = this.getMyBuildingForMp();
        if (myBuildingOnMp != null) {
            this.buildMyPin();
            this.myPin.updateNftTexture(myBuildingOnMp.imageUrl.value);
        }

        this.createMapZipCodes();
    }

    private getMyBuildingForMp(): IBuildingModel {
        return this.playerModel.buildings.value.find(building => ((building?.area.value != null) && building.area.value === this.mapZoneConfig.area) && building.marketplaceId.value != null);
    }

    private buildMyPin(): void {
        this.myPin = new MyMarketplacePin(this.marketplaceResources);
        this.myPin.anchor.set(0.5);
        this.myPin.eventMode = "static";
        this.myPin.on("pointerdown", () => this.onMapZonePinClick.emit());
        this.myPin.position = this.mapZoneConfig.mapZoneDescription.myPinPosition;
        this.pinsContainer.addChild(this.myPin);
    }

    private async addedToStage(): Promise<void> {
        this.selectedSprite.visible = false;
        this.selectedSprite.alpha = 0;
        this.zipCodesContainer.visible = false;
        this.zipCodesContainer.alpha = 0;
        this.zipCodePinsContainer.visible = false;
        this.zipCodePinsContainer.alpha = 0;
        this.zoomTargetPositionProp = new Point(this.width * 0.5, this.height * 0.5);
        if (this.mapZoneConfig.area === this.marketplaceMapViewModel.defaultSelectedZoneId.value) {
            this.isSelected = true;
            this.selectedSprite.visible = true;
            this.selectedSprite.alpha = 1;
        }
    }

    private createMapZipCodes(): void {
        this.mapZoneConfig.mapZoneZipCodesDescription.forEach((zipCodeDescription) => {
            const zipCode = new MapZipCode(
                this.marketplaceResources,
                this.marketplaceController,
                this.marketplaceMapModel,
                this.playerModel,
                zipCodeDescription,
                this.mapZoneConfig.area,
                this.zipCodePinsContainer
            );
            zipCode.scale.set(MapZipCode.DEFAULT_SCALE);
            zipCode.position.set(zipCodeDescription.zipCodePosition.x, zipCodeDescription.zipCodePosition.y);
            zipCode.onZipCodePinClick.add(this.mapZipCodeClickHandle.bind(this, zipCode));
            this.zipCodes.push(zipCode);
            this.zipCodesContainer.addChild(zipCode);
        });
    }

    private async mapZipCodeClickHandle(zipCode: MapZipCode): Promise<void> {
        this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Click on zip code yellow pin in specific area map", this.analyticsProvider.currentEventIndex));
        this.zipCodes.forEach(async item => {
            const isSelected = zipCode === item;
            item.selectedHandle(isSelected);
            if (isSelected) {
                this.zipCodesContainer.setChildIndex(item, this.zipCodesContainer.children.length - 1);
            }
        });
    }

    public async focusIn(isSelectedNewValue: boolean): Promise<void> {
        if (!this.isSelected && isSelectedNewValue) {
            this.marketplaceController.updateMapFilter(new MapFilter(this.mapZoneConfig.area));
        }
        this.isSelected = isSelectedNewValue;
        this.focusInMapZoneAnimation();
    }

    public async focusOut(): Promise<void> {
        if (this.isSelected && this.marketplaceMapViewModel.currentMapFilter.value.zipCode != null) {
            this.marketplaceController.updateMapFilter(new MapFilter(this.mapZoneConfig.area));
        }
        this.zipCodes.forEach(zipCode => zipCode.selectedHandle(false));
        const alpha = this.isSelected ? 1 : 0;
        this.focusOutAnimation.to(this.zipCodesContainer, { alpha: 0, duration: 0.3 });
        this.focusOutAnimation.to(this.mapZoneBg, { alpha: 0, duration: 0.3 }, "<");
        this.focusOutAnimation.to(this.zipCodePinsContainer, { alpha: 0, duration: 0.3 }, "<");
        this.focusOutAnimation.to(this.corner, { alpha: 1, duration: 0.2 }, "<");
        this.focusOutAnimation.to(this.selectedSprite, { visible: this.isSelected, alpha: alpha, duration: 0.4 });
        if (this.pin != null) {
            this.focusOutAnimation.to(this.pin, { alpha: 1, duration: 0.6, onStart: () => { this.pin.visible = this.pinIsAvailable } }, "<75%");
        }
        if (this.myPin != null) {
            this.focusOutAnimation.to(this.myPin, { alpha: 1, duration: 0.6 }, "<");
        }
        this.focusOutAnimation.set(this.zipCodesContainer, { visible: false });
        this.focusOutAnimation.set(this.zipCodePinsContainer, { visible: false });
        await this.focusOutAnimation.play();
        this.focusOutAnimation.clear();
    }

    private async focusInMapZoneAnimation(): Promise<void> {
        const alpha = this.isSelected ? 1 : 0;
        this.focusInAnimation.set(this.selectedSprite, { visible: this.isSelected });
        if (this.pin != null) {
            this.focusInAnimation.to(this.pin, { alpha: 0, duration: 0.6 });
        }
        if (this.myPin != null) {
            this.focusInAnimation.to(this.myPin, { alpha: 0, duration: 0.6 }, "<");
        }
        this.focusInAnimation.to(this.selectedSprite, { alpha: alpha, duration: 0.2 }, "<");
        this.focusInAnimation.to(this.selectedSprite, { alpha: 0, duration: 0.3 }, ">0.35");
        this.focusInAnimation.to(this.zipCodesContainer, { visible: this.isSelected, alpha: alpha, duration: 0.3 }, "<75%");
        this.focusInAnimation.to(this.zipCodePinsContainer, { visible: this.isSelected, alpha: alpha, duration: 0.4 }, "<");
        this.focusInAnimation.to(this.mapZoneBg, { alpha: alpha, duration: 0.4 }, "<");
        this.focusInAnimation.to(this.corner, { alpha: !this.isSelected ? 1 : 0, duration: 0.2 }, "<");
        this.focusInAnimation.set(this.selectedSprite, { visible: false });
        if (this.pin != null) {
            this.focusInAnimation.set(this.pin, { visible: false });
        }
        await this.focusInAnimation.play();
        this.focusInAnimation.clear();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.marketplaceMapModel.propertyChanged.remove(this.onMarketplaceMapModelPropertyChanged, this);
        this.zipCodes.forEach(zipCodes => zipCodes.onZipCodePinClick.removeAll());
        this.pin.off("pointerdown");
        this.myPin?.off("pointerdown");
        this.focusOutAnimation?.kill();
        this.focusInAnimation?.kill();
        super.destroy(options);
    }
}
